import { Map as ImMap, List, type RecordOf } from 'immutable';

// import { institutionsTypes } from '../institutions';
import { defaultCacheToState } from 'app-components/utils/persist/getCachedState';
import { defaultStateToCache } from 'app-components/utils/persist/sagas';

import { featureFlagsSelectors } from '../feature-flags';
import * as types from './institutionLoginsTypes';

// QCS differs slightly from our record, use this type only inside transformers
export type QCSInstitutionLogin = Omit<types.InstitutionLoginProps, 'aggregators' | 'fiMigrationInfo'> & {
  aggregators: (types.InstitutionLoginAggregator & { cpChannel?: string })[];
} & {
  fiMigrationInfo?: {
    toFI: { institutionId: string; channel: string };
    fiMigrationType?: types.FiMigrationType | null;
  } | null;
};

const transformOptionalAction = (qLogin: QCSInstitutionLogin) => {
  if (
    qLogin &&
    qLogin.optionalAction === 'FI_MIGRATION' &&
    qLogin.fiMigrationInfo &&
    qLogin.institutionId === qLogin.fiMigrationInfo?.toFI?.institutionId &&
    qLogin.channel === qLogin.fiMigrationInfo?.toFI?.channel
  ) {
    return undefined;
  }
  return qLogin?.optionalAction;
};

export const transformQcsInstitutionLoginToInstitutionLogin = (
  qLogin: QCSInstitutionLogin,
  existingResource?: types.InstitutionLoginProps | RecordOf<types.InstitutionLoginProps>,
) => {
  let aggregators: List<types.InstitutionLoginAggregator> | null = null;
  if (qLogin.aggregators || existingResource?.aggregators) {
    const connectedAggregators = (qLogin.aggregators || existingResource?.aggregators)?.map(
      (aggregator: types.InstitutionLoginAggregator & { cpChannel?: string }) =>
        types.mkInstitutionLoginAggregator({
          ...aggregator,
          channel: aggregator.cpChannel ?? aggregator.channel ?? 'FDP_DEFAULT',
        }),
    );
    if (connectedAggregators?.length > 0) {
      aggregators = List(connectedAggregators);
    }
  }

  return types.mkInstitutionLogin({
    ...((existingResource && 'toJS' in existingResource
      ? existingResource.toJS()
      : (existingResource ?? {})) as types.InstitutionLoginProps),
    ...(qLogin ?? {}),

    id: qLogin.id || existingResource?.id,

    optionalAction: transformOptionalAction(qLogin || existingResource),

    fiMigrationInfo: qLogin?.fiMigrationInfo
      ? types.mkFiMigrationInfo({
          toInstitutionId: qLogin.fiMigrationInfo.toFI.institutionId,
          toChannel: qLogin.fiMigrationInfo.toFI.channel,
          fiMigrationType: qLogin.fiMigrationInfo.fiMigrationType,
        })
      : existingResource?.fiMigrationInfo || undefined,

    aggregators,

    // Grabbing this in saga via a separate action to ensure latest data. Long term QCS will fix this.
    fiIssue: undefined, // qLogin.fiIssue ? institutionsTypes.mkFiIssue(qLogin.fiIssue) : null,

    ewcLive: qLogin?.ewcLive ?? false,
  });
};

export const transformQcsInstitutionLoginsToInstitutionLogins = (qLogins: QCSInstitutionLogin[]) =>
  qLogins.map((login) => transformQcsInstitutionLoginToInstitutionLogin(login));

export const transformResponseToInstitutionLogins = (
  response?: { data?: { resources?: types.InstitutionLoginProps[] } } | null,
) => transformQcsInstitutionLoginsToInstitutionLogins(response?.data?.resources as unknown as QCSInstitutionLogin[]);

export const transformLoginResourceWithQCSResponse = (
  resource: types.InstitutionLoginProps | RecordOf<types.InstitutionLoginProps>,
  response: { data?: { id?: string; resource?: types.InstitutionLoginProps } },
) =>
  transformQcsInstitutionLoginToInstitutionLogin(
    {
      ...((response.data?.resource || {}) as QCSInstitutionLogin),
      id: response.data?.id || response.data?.resource?.id || resource?.id,
    },
    resource,
  );

export const transformInstitutionLoginToRequestData = (institutionLogin: types.InstitutionLoginProps) => ({
  clientId: institutionLogin.clientId,
  id: institutionLogin.id,
  isDeleted: institutionLogin.isDeleted,
  cpSetupMode: 'SKIP_ACCOUNT_DISCOVERY',
  channel: institutionLogin.channel,
  name: institutionLogin.name,
  refreshFrequency: institutionLogin.refreshFrequency,
  aggregators:
    !institutionLogin.aggregators || institutionLogin.aggregators.size === 0
      ? undefined
      : ([
          {
            cpChannel: institutionLogin.aggregators.get(0)?.channel,
            cpId: institutionLogin.aggregators.get(0)?.cpId,
          },
        ] as unknown as List<types.InstitutionLoginAggregator>), // Using list type to satisfy typescript transformer type

  // todo: should we support clearing of brandingId and/or institutionId - (deactivation)
  // todo: what about setting of brandingId and/or institutionId - (activation)
  // todo: what about changing of brandingId and/or institutionId - (i.e. deactivation - reactivation)
});

export const transformRefreshingLoginsToRequestData = (
  logins: types.InstitutionLoginProps[],
  credentialsBlobsForRefreshingLogins: types.InstitutionLoginStore['credentialBlobsForRefreshingLogins'],
  credentialsForRefreshingLogins: types.InstitutionLoginStore['credentialsForRefreshingLogins'],
) => {
  const loginRefreshCredentials: Array<{
    institutionLoginId: string;
    credentialsBlob?: string;
    credentials?: Array<types.Credential & { name: string }>;
  }> = [];
  if (credentialsBlobsForRefreshingLogins.size > 0) {
    credentialsBlobsForRefreshingLogins.forEach((credentialsBlob, institutionLoginId) => {
      if (logins.find((login) => login.id === institutionLoginId)) {
        loginRefreshCredentials.push({
          institutionLoginId,
          credentialsBlob,
        });
      }
    });
  }
  if (credentialsForRefreshingLogins.size > 0) {
    credentialsForRefreshingLogins.forEach((credentials, institutionLoginId) => {
      if (logins.find((login) => login.id === institutionLoginId)) {
        const tCredentials = credentials.toArray().map((credential: types.Credential) => ({
          name: credential.key,
          key: credential.key,
          value: credential.value,
        }));
        loginRefreshCredentials.push({
          institutionLoginId,
          credentials: tCredentials,
        });
      }
    });
  }
  return {
    ...(featureFlagsSelectors.dangerouslyUseFeatureFlags()?.get('ignoreRefreshThrottle') && { doIgnoreThrottle: true }),
    loginRefreshCredentials: loginRefreshCredentials.length > 0 ? loginRefreshCredentials : null,
    ...(featureFlagsSelectors.dangerouslyUseFeatureFlags()?.get('investmentTransactions') && {
      investmentAggregationType: 'FULL_AGGREGATION',
    }),
  };
};

export type QCSChallengeItem = types.MfaChallengeItem & { displayText: string; image: string };

export const transformMfaResponseToMfaChallenge = (
  institutionLoginId: string,
  mfaChallenges: QCSChallengeItem[],
  submitUrl: string,
) =>
  types.mkMfaChallenge({
    institutionLoginId,
    challenges: List(
      mfaChallenges.map((challenge) => {
        if (challenge.type === 'IMAGE') {
          return types.mkMfaChallengeItem({
            type: challenge.type,
            key: challenge.key,
            sequence: challenge.sequence,
            question: challenge.displayText,
            base64Image: challenge.image,
            answerChoices: !challenge.answerChoices
              ? null
              : List(
                  challenge.answerChoices.map((answerChoice) =>
                    types.mkMfaAnswerChoice({
                      displayText: answerChoice.displayText,
                    }),
                  ),
                ),
          });
        }
        return types.mkMfaChallengeItem({
          type: challenge.type,
          key: challenge.key,
          sequence: challenge.sequence,
          question: challenge.question,
          answerChoices: !challenge.answerChoices
            ? null
            : List(
                challenge.answerChoices.map((answerChoice) =>
                  types.mkMfaAnswerChoice({
                    displayText: answerChoice.displayText,
                  }),
                ),
              ),
        });
      }),
    ),
    submitUrl,
  });

export const transformActionParamsToRequestData = (action: types.InstitutionLoginQcsAction, params: any) => {
  if (action === 'disconnect') {
    const data: any = {};
    data.doSynchronous = !!(params && params.doSynchronous);
    return data;
  }
  return null;
};

// /////////////////////////////////////////////////////////////////////////////
// Transformers for Refresh Status
// /////////////////////////////////////////////////////////////////////////////

type QCSRefreshAggregator = Omit<types.RefreshAggregator, 'accounts'> & { cpChannel?: string };

export type QCSRefreshResponse = Omit<types.RefreshResponse, 'institutionLogins'> & {
  institutionLogins: (Omit<types.RefreshInstitutionLogin, 'aggregators' | 'submitUrl'> & {
    pollingReference?: string;
    // Account discovery uses channel while the institution-login refresh poll can use cpChannel.
    aggregators?: QCSRefreshAggregator[] | null;
  })[];
};

export function transformResponseToRefreshStatus(response: QCSRefreshResponse): RecordOf<types.RefreshResponse> {
  return types.makeRefreshResponse({
    jobId: response.jobId,

    isProcessing: response.isProcessing,
    status: response.status,

    institutionLogins: List(
      response.institutionLogins.map((login) =>
        types.makeRefreshInstitutionLogin({
          id: login.id,
          isProcessing: login.isProcessing,
          status: login.status,
          credentialsBlob: login.credentialsBlob,
          submitUrl: login.pollingReference,
          aggregators: !login.aggregators
            ? null
            : List(
                login.aggregators.map((aggregator) =>
                  types.makeRefreshAggregator({
                    channel: aggregator.channel ?? aggregator.cpChannel,

                    isProcessing: aggregator.isProcessing,
                    aggStatus: aggregator.aggStatus,
                    cpAggStatusCode: aggregator.cpAggStatusCode,
                    cpAggStatusDetail: aggregator.cpAggStatusDetail,

                    mfaChallenges: aggregator.mfaChallenges,
                  }),
                ),
              ),
        }),
      ),
    ),
  });
}

// Modify Persister defaults
export const stateToCacheInstitutionLogins = (state: any): any => {
  const stateToPersist = state
    .set('dismissedFiIssuesByLoginId', ImMap())
    .set('dismissedAggStatusesByLoginId', ImMap())
    .set('fiMigrationBannerDismissed', false);
  return defaultStateToCache(stateToPersist);
};

export const cacheToStateInstitutionLogins = (cachedState: any): any => {
  const state = defaultCacheToState(cachedState);
  return state
    .set('dismissedFiIssuesByLoginId', ImMap())
    .set('dismissedAggStatusesByLoginId', ImMap())
    .set('fiMigrationBannerDismissed', false);
};
